import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const PLANOS = { '30_dias':299, '90_dias':749, '180_dias':1299, '365_dias':1999 };
const COR = {
  aprovado:            { bg:'bg-green-50  text-green-700  border-green-200',  dot:'bg-green-500' },
  aguardando_analise:  { bg:'bg-orange-50 text-orange-700 border-orange-200', dot:'bg-orange-400' },
  pendente:            { bg:'bg-gray-50   text-gray-500   border-gray-200',   dot:'bg-gray-400' },
  rejeitado:           { bg:'bg-red-50    text-red-600    border-red-200',    dot:'bg-red-500' },
  estornado:           { bg:'bg-purple-50 text-purple-600 border-purple-200', dot:'bg-purple-400' },
};

function Money({ val = 0 }) {
  return <>R$ {Number(val).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</>;
}

export default function AdminFinanceiro() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('30'); // dias

  useEffect(() => { carregar(); }, [periodo]);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/financeiro?dias=${periodo}`);
      setDados(data);
    } catch { setDados(null); }
    setLoading(false);
  };

  const resumo = dados?.resumo || {};
  const pagamentos = dados?.pagamentos || [];

  const CARDS = [
    { label:'Receita Total (aprovada)', val:resumo.receita_total||0,   cor:'from-acre-green to-acre-teal', icon:'💰', money:true },
    { label:'Pagamentos Aprovados',     val:resumo.total_aprovados||0, cor:'from-blue-500 to-blue-600',   icon:'✅', money:false },
    { label:'Aguardando Aprovação',     val:resumo.total_pendentes||0, cor:'from-orange-400 to-orange-500',icon:'⏳', money:false },
    { label:'Rejeitados/Cancelados',    val:resumo.total_rejeitados||0,cor:'from-red-400 to-red-500',     icon:'❌', money:false },
  ];

  return (
    <Layout
      title="💰 Painel Financeiro"
      subtitle="Estatísticas de receitas e pagamentos"
      action={
        <div className="flex gap-2">
          {[
            {v:'7',  l:'7 dias'},
            {v:'30', l:'30 dias'},
            {v:'90', l:'3 meses'},
            {v:'365',l:'1 ano'},
          ].map(({v,l}) => (
            <button key={v} onClick={() => setPeriodo(v)}
              className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all active:scale-95
                ${periodo===v ? 'bg-acre-green text-white border-acre-green' : 'bg-white text-gray-500 border-gray-200 hover:border-acre-green hover:text-acre-green'}`}>
              {l}
            </button>
          ))}
        </div>
      }>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-acre-green border-t-transparent rounded-full animate-spin"/>
        </div>
      ) : (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {CARDS.map((c,i) => (
              <div key={i} className={`rounded-2xl p-4 bg-gradient-to-br ${c.cor} text-white shadow-sm`}>
                <div className="text-2xl mb-1">{c.icon}</div>
                <p className="font-sora font-bold text-2xl leading-none">
                  {c.money ? <><span className="text-sm font-normal">R$</span> {Number(c.val).toLocaleString('pt-BR',{minimumFractionDigits:2})}</> : c.val}
                </p>
                <p className="text-white/70 text-xs mt-1 leading-tight">{c.label}</p>
              </div>
            ))}
          </div>

          {/* Gráfico simples de barras por plano */}
          <div className="card p-5 mb-5">
            <h3 className="section-title mb-4">📊 Receita por Plano</h3>
            <div className="space-y-3">
              {Object.entries(PLANOS).map(([plano, preco]) => {
                const qtd = dados?.por_plano?.[plano] || 0;
                const total = qtd * preco;
                const max = Math.max(...Object.values(PLANOS).map((p,_,a) => p * (dados?.por_plano?.[Object.keys(PLANOS)[Object.values(PLANOS).indexOf(p)]] || 0)));
                const pct = max > 0 ? Math.round((total / max) * 100) : 0;
                const nomes = { '30_dias':'30 Dias', '90_dias':'3 Meses', '180_dias':'6 Meses', '365_dias':'1 Ano' };
                return (
                  <div key={plano}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-gray-600 font-semibold">{nomes[plano]}</span>
                      <span className="text-gray-400">{qtd} contrato(s) · <span className="text-acre-green font-semibold"><Money val={total}/></span></span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-acre-green to-acre-teal rounded-full transition-all duration-700"
                        style={{ width: `${Math.max(pct, total>0?8:0)}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cadastros */}
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            {[
              { label:'Candidatos cadastrados', val:dados?.cadastros?.candidatos||0, icon:'👤', c:'text-acre-green' },
              { label:'Empresas cadastradas',   val:dados?.cadastros?.empresas||0,   icon:'🏢', c:'text-blue-600' },
              { label:'Vagas publicadas',        val:dados?.cadastros?.vagas||0,      icon:'📋', c:'text-purple-600' },
            ].map((s,i) => (
              <div key={i} className="card p-4 flex items-center gap-3">
                <span className="text-3xl">{s.icon}</span>
                <div>
                  <p className={`font-sora font-bold text-2xl ${s.c}`}>{s.val}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Lista de pagamentos */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="section-title">Histórico de Pagamentos</h3>
              <span className="text-xs text-gray-400">{pagamentos.length} registro(s)</span>
            </div>
            {pagamentos.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">Nenhum pagamento no período.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {pagamentos.map(p => {
                  const st = COR[p.status] || COR.pendente;
                  return (
                    <div key={p.id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${st.dot}`}/>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-acre-dark truncate">{p.razao_social}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(p.data_solicitacao).toLocaleDateString('pt-BR')}
                          {' · '}{p.plano?.replace(/_/g,' ')}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-acre-green">
                          {p.valor ? <Money val={p.valor}/> : <Money val={PLANOS[p.plano]||0}/>}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${st.bg}`}>
                          {p.status?.replace(/_/g,' ')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}
